// apps/web/src/components/reports/team-performance/TeamPerformanceTable.tsx
/**
 * Team Performance — the results table.
 *
 * Same header chrome, row height, sticky-column shadows and colour tokens as
 * AgentPerformanceTable, so every report in this module reads as one product.
 */
import { type JSX } from 'react'

import {
  TEAM_PERFORMANCE_COLUMNS,
  type ReportColumnDef,
  formatDuration,
  formatPercent,
} from './columns.js'
import type { TeamPerformanceRow } from '../../../hooks/useTeamPerformanceReport.js'

interface TeamPerformanceTableProps {
  rows: TeamPerformanceRow[]
  visibleColumns: string[]
}

function Blank(): JSX.Element {
  return <span className="text-slate-300">—</span>
}

function cellFor(row: TeamPerformanceRow, key: string): JSX.Element {
  switch (key) {
    case 'teamName':
      return (
        <span className="truncate font-semibold text-slate-900" title={row.teamName}>
          {row.teamName}
        </span>
      )
    case 'avgResponseSeconds':
      return <span className="tabular-nums">{formatDuration(row.avgResponseSeconds)}</span>
    case 'avgResolutionSeconds':
      return <span className="tabular-nums">{formatDuration(row.avgResolutionSeconds)}</span>
    case 'responseSlaPct':
      return <SlaPercent value={row.responseSlaPct} />
    case 'resolutionSlaPct':
      return <SlaPercent value={row.resolutionSlaPct} />
    default: {
      const value = (row as unknown as Record<string, unknown>)[key]
      if (typeof value !== 'number') return <Blank />
      return (
        <span className={`tabular-nums ${value === 0 ? 'text-slate-300' : 'text-slate-700'}`}>
          {value}
        </span>
      )
    }
  }
}

function SlaPercent({ value }: { value: number | null }): JSX.Element {
  if (value === null) return <Blank />
  const tone =
    value >= 90 ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : value >= 75 ? 'bg-amber-50 text-amber-700 ring-amber-200'
        : 'bg-red-50 text-red-700 ring-red-200'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ring-1 ring-inset ${tone}`}>
      {formatPercent(value)}
    </span>
  )
}

export default function TeamPerformanceTable({
  rows,
  visibleColumns,
}: TeamPerformanceTableProps): JSX.Element {
  const byKey = new Map(TEAM_PERFORMANCE_COLUMNS.map((c) => [c.key, c]))

  const ordered: ReportColumnDef[] = [
    ...TEAM_PERFORMANCE_COLUMNS.filter((c) => c.locked && visibleColumns.includes(c.key)),
    ...visibleColumns
      .map((k) => byKey.get(k))
      .filter((c): c is ReportColumnDef => c !== undefined && !c.locked),
  ]
  const cols = ordered

  let stickyAcc = 0
  const laidOut = cols.map((c) => {
    if (c.stickyLeft === undefined) return c
    const stickyLeft = stickyAcc
    stickyAcc += c.width
    return { ...c, stickyLeft }
  })
  const lastStickyKey = [...laidOut].reverse().find((c) => c.stickyLeft !== undefined)?.key

  return (
    <table
      className="w-full min-w-max border-separate border-spacing-0 text-[13px]"
      style={{ tableLayout: 'fixed' }}
    >
      <thead>
        <tr>
          {laidOut.map((col) => (
            <th
              key={col.key}
              style={{
                width: col.width,
                minWidth: col.width,
                ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
              }}
              className={`relative sticky top-0 h-11 border-b border-slate-200 bg-[#FAFBFC] px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500 ${
                col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
              } ${col.stickyLeft !== undefined ? 'z-30' : 'z-20'} ${
                col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(229,231,235)]' : ''
              }`}
            >
              <span
                className={`inline-flex items-center gap-1.5 ${
                  col.align === 'right' ? 'flex-row-reverse' : ''
                }`}
              >
                {col.icon && <col.icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                <span className="truncate">{col.label}</span>
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const cellBg = 'bg-white group-hover:bg-[#F7F9FC]'
          return (
            <tr key={`${row.teamId}:${i}`} className="group">
              {laidOut.map((col) => (
                <td
                  key={col.key}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                    ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
                  }}
                  className={`h-[54px] border-b border-slate-100 px-4 align-middle text-slate-700 transition-colors ${cellBg} ${
                    col.stickyLeft !== undefined ? 'sticky z-10' : ''
                  } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${
                    col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(243,244,246)]' : ''
                  }`}
                >
                  <div
                    className={
                      col.align === 'center'
                        ? 'flex items-center justify-center'
                        : col.align === 'right'
                          ? 'flex items-center justify-end'
                          : 'truncate'
                    }
                  >
                    {cellFor(row, col.key)}
                  </div>
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
